import { createAsyncThunk } from "@reduxjs/toolkit";
import apiCall from "../AuthorizedApi";
import type {
  WhatsAppAccountsResponse,
  WhatsAppConversationsResponse,
  WhatsAppConversationsRequestPayload,
  WhatsAppMessagesResponse,
  WhatsAppMessagesRequestPayload,
  WhatsAppSendMessageRequestPayload,
  WhatsAppSendMessageResponse,
  WhatsAppCampaign,
  WhatsAppTemplate,
  CreateWhatsAppCampaignPayload,
  CreateWhatsAppTemplatePayload,
  StudioAlbumItem,
  RedeemUserItem,
  CampaignAudiencePreview,
  WalletEstimateResponse,
  WalletBalanceResponse,
  WalletTransactionsResponse,
  WalletTransactionsRequestPayload,
  WalletPlansResponse,
  CreateRechargeOrderResponse,
  CreateRechargeOrderPayload,
  VerifyRechargeResponse,
  VerifyRechargePayload,
  CancelRechargeResponse,
  CancelRechargePayload,
  RechargeOrdersListResponse,
  RechargeOrdersListPayload,
} from "../../../Data/whatsapp.dto";

function getMessage(err: unknown): string {
  const e = err as {
    response?: {
      data?: any;
      status?: number;
    };
    message?: string;
  };

  const data = e?.response?.data;
  if (!data) {
    if (e?.message) return e.message;
    return "Something went wrong. Please check your internet connection and try again.";
  }

  if (typeof data === "string") return data;

  if (data.wallet_error && typeof data.wallet_error === "string") {
    return `Wallet Error: ${data.wallet_error}`;
  }
  if (data.detail && typeof data.detail === "string") return data.detail;
  if (data.message && typeof data.message === "string") return data.message;
  if (data.error && typeof data.error === "string") return data.error;

  if (typeof data === "object") {
    const errorMessages: string[] = [];
    for (const [key, value] of Object.entries(data)) {
      if (Array.isArray(value) && value.length > 0) {
        const readableKey = key === "non_field_errors" ? "" : `${key.replace(/_/g, " ")}: `;
        errorMessages.push(`${readableKey}${value.join(", ")}`);
      } else if (typeof value === "string") {
        const readableKey = key === "non_field_errors" ? "" : `${key.replace(/_/g, " ")}: `;
        errorMessages.push(`${readableKey}${value}`);
      }
    }
    if (errorMessages.length > 0) {
      return errorMessages.join(". ");
    }
  }

  return "An unexpected error occurred. Please try again.";
}

function buildQueryString(params: Record<string, string | number | boolean | undefined>): string {
  const parts: string[] = [];
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null) {
      parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
    }
  }
  return parts.length > 0 ? `?${parts.join("&")}` : "";
}

export const getWhatsAppAccounts = createAsyncThunk(
  "whatsapp/getAccounts",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiCall({
        method: "GET",
        url: "/api/v1/whatsapp/accounts/",
        headers: { "Content-Type": "application/json" },
      });
      return (response as WhatsAppAccountsResponse);
    } catch (error: unknown) {
      return rejectWithValue({ message: getMessage(error) });
    }
  }
);

export const getWalletBalance = createAsyncThunk(
  "whatsapp/getWalletBalance",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiCall({
        method: "GET",
        url: "/api/v1/wallet/balance/",
        headers: { "Content-Type": "application/json" },
      });
      return (response as WalletBalanceResponse);
    } catch (error: unknown) {
      return rejectWithValue({ message: getMessage(error) });
    }
  }
);

export const getWhatsAppConversations = createAsyncThunk(
  "whatsapp/getConversations",
  async (payload: WhatsAppConversationsRequestPayload | undefined, { rejectWithValue }) => {
    try {
      const params = {
        page: payload?.page ?? 1,
        page_size: payload?.page_size ?? 20,
        is_active: payload?.is_active,
      };
      const qs = buildQueryString(params);
      const response = await apiCall({
        method: "GET",
        url: `/api/v1/whatsapp/conversations/${qs}`,
        headers: { "Content-Type": "application/json" },
      });
      return (response as WhatsAppConversationsResponse);
    } catch (error: unknown) {
      return rejectWithValue({ message: getMessage(error) });
    }
  }
);

export const getWhatsAppMessages = createAsyncThunk(
  "whatsapp/getMessages",
  async (payload: WhatsAppMessagesRequestPayload, { rejectWithValue }) => {
    try {
      const params = {
        conversation_id: payload.conversation_id,
        customer_number: payload.customer_number,
        page: payload.page ?? 1,
        page_size: payload.page_size ?? 20,
      };
      const qs = buildQueryString(params);
      const response = await apiCall({
        method: "GET",
        url: `/api/v1/whatsapp/messages/${qs}`,
        headers: { "Content-Type": "application/json" },
      });
      return (response as WhatsAppMessagesResponse);
    } catch (error: unknown) {
      return rejectWithValue({ message: getMessage(error) });
    }
  }
);

export const sendWhatsAppMessage = createAsyncThunk(
  "whatsapp/sendMessage",
  async (payload: WhatsAppSendMessageRequestPayload, { rejectWithValue }) => {
    try {
      const response = await apiCall({
        method: "POST",
        url: "/api/v1/whatsapp/send/",
        headers: { "Content-Type": "application/json" },
        data: payload,
      });
      return {
        response: response as WhatsAppSendMessageResponse,
        payload,
      };
    } catch (error: unknown) {
      return rejectWithValue({ message: getMessage(error) });
    }
  }
);

export const getWalletTransactions = createAsyncThunk(
  "whatsapp/getWalletTransactions",
  async (payload: WalletTransactionsRequestPayload | undefined, { rejectWithValue }) => {
    try {
      const params: Record<string, any> = {
        page: payload?.page ?? 1,
        page_size: payload?.page_size ?? 20,
      };
      if (payload?.type) {
        params.type = payload.type;
      }
      if (payload?.category) {
        params.category = payload.category;
      }
      const qs = buildQueryString(params);
      const response = await apiCall({
        method: "GET",
        url: `/api/v1/wallet/transactions/${qs}`,
        headers: { "Content-Type": "application/json" },
      });
      return (response as WalletTransactionsResponse);
    } catch (error: unknown) {
      return rejectWithValue({ message: getMessage(error) });
    }
  }
);

export const getWalletPlans = createAsyncThunk(
  "whatsapp/getWalletPlans",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiCall({
        method: "GET",
        url: "/api/v1/wallet/plans/?is_active=false",
        headers: { "Content-Type": "application/json" },
      });
      return (response as WalletPlansResponse);
    } catch (error: unknown) {
      return rejectWithValue({ message: getMessage(error) });
    }
  }
);

export const getWhatsAppCampaigns = createAsyncThunk(
  "whatsapp/getCampaigns",
  async (statusFilter: string | undefined, { rejectWithValue }) => {
    try {
      const qs = statusFilter && statusFilter !== "all" ? `?status=${encodeURIComponent(statusFilter)}` : "";
      const response = await apiCall({
        method: "GET",
        url: `/api/v1/whatsapp/campaigns/${qs}`,
        headers: { "Content-Type": "application/json" },
      });
      const resData = (response as any)?.data;
      let list: WhatsAppCampaign[] = [];
      if (Array.isArray(resData?.campaigns)) {
        list = resData.campaigns;
      } else if (Array.isArray(resData)) {
        list = resData;
      } else if (Array.isArray((response as any)?.results)) {
        list = (response as any).results;
      } else if (Array.isArray((response as any)?.campaigns)) {
        list = (response as any).campaigns;
      } else if (Array.isArray(response)) {
        list = response as WhatsAppCampaign[];
      }
      return list;
    } catch (error: unknown) {
      return rejectWithValue({ message: getMessage(error) });
    }
  }
);

export const getWhatsAppCampaignDetail = createAsyncThunk(
  "whatsapp/getCampaignDetail",
  async (campaignId: string, { rejectWithValue }) => {
    try {
      const response = await apiCall({
        method: "GET",
        url: `/api/v1/whatsapp/campaigns/${campaignId}/`,
        headers: { "Content-Type": "application/json" },
      });
      const data =
        (response as any)?.data?.campaign ??
        (response as any)?.data ??
        response;
      return data as WhatsAppCampaign;
    } catch (error: unknown) {
      return rejectWithValue({ message: getMessage(error) });
    }
  }
);

export const createWhatsAppCampaign = createAsyncThunk(
  "whatsapp/createCampaign",
  async (payload: CreateWhatsAppCampaignPayload, { rejectWithValue }) => {
    try {
      const response = await apiCall({
        method: "POST",
        url: "/api/v1/whatsapp/campaigns/",
        headers: { "Content-Type": "application/json" },
        data: payload,
      });
      const data =
        (response as any)?.data?.campaign ??
        (response as any)?.data ??
        response;
      return data as WhatsAppCampaign;
    } catch (error: unknown) {
      return rejectWithValue({ message: getMessage(error) });
    }
  }
);

export const launchWhatsAppCampaign = createAsyncThunk(
  "whatsapp/launchCampaign",
  async (campaignId: string, { rejectWithValue }) => {
    try {
      const response = await apiCall({
        method: "POST",
        url: `/api/v1/whatsapp/campaigns/${campaignId}/launch/`,
        headers: { "Content-Type": "application/json" },
      });
      const data =
        (response as any)?.data?.campaign ??
        (response as any)?.data ??
        response;
      return data as WhatsAppCampaign;
    } catch (error: unknown) {
      return rejectWithValue({ message: getMessage(error) });
    }
  }
);

export const getStudioAlbums = createAsyncThunk(
  "whatsapp/getStudioAlbums",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiCall({
        method: "GET",
        url: "/api/v1/whatsapp/campaigns/studio-albums/",
        headers: { "Content-Type": "application/json" },
      });
      const resData = (response as any)?.data;
      let list: StudioAlbumItem[] = [];
      if (Array.isArray(resData?.albums)) {
        list = resData.albums;
      } else if (Array.isArray(resData?.projects)) {
        list = resData.projects;
      } else if (Array.isArray(resData)) {
        list = resData;
      } else if (Array.isArray((response as any)?.results)) {
        list = (response as any).results;
      } else if (Array.isArray((response as any)?.albums)) {
        list = (response as any).albums;
      } else if (Array.isArray(response)) {
        list = response as StudioAlbumItem[];
      }
      return list;
    } catch (error: unknown) {
      return rejectWithValue({ message: getMessage(error) });
    }
  }
);

export const getRedeemUsers = createAsyncThunk(
  "whatsapp/getRedeemUsers",
  async (
    params:
      | { album_id?: number | string; album_ids?: Array<number | string> | string }
      | undefined,
    { rejectWithValue }
  ) => {
    try {
      const queryParams: Record<string, string | number> = {};
      if (params?.album_id) queryParams.album_id = params.album_id;
      if (params?.album_ids) {
        queryParams.album_ids = Array.isArray(params.album_ids)
          ? params.album_ids.join(",")
          : params.album_ids;
      }
      const qs = buildQueryString(queryParams);
      const response = await apiCall({
        method: "GET",
        url: `/api/v1/whatsapp/campaigns/redeem-users/${qs}`,
        headers: { "Content-Type": "application/json" },
      });
      const resData = (response as any)?.data;
      let list: RedeemUserItem[] = [];
      if (Array.isArray(resData?.users)) {
        list = resData.users;
      } else if (Array.isArray(resData?.redeem_users)) {
        list = resData.redeem_users;
      } else if (Array.isArray(resData)) {
        list = resData;
      } else if (Array.isArray((response as any)?.results)) {
        list = (response as any).results;
      } else if (Array.isArray(response)) {
        list = response as RedeemUserItem[];
      }
      return list;
    } catch (error: unknown) {
      return rejectWithValue({ message: getMessage(error) });
    }
  }
);

export const getCampaignAudience = createAsyncThunk(
  "whatsapp/getCampaignAudience",
  async (
    params:
      | { album_id?: number | string; album_ids?: Array<number | string> | string }
      | undefined,
    { rejectWithValue }
  ) => {
    try {
      const queryParams: Record<string, string | number> = {};
      if (params?.album_id) queryParams.album_id = params.album_id;
      if (params?.album_ids) {
        queryParams.album_ids = Array.isArray(params.album_ids)
          ? params.album_ids.join(",")
          : params.album_ids;
      }
      const qs = buildQueryString(queryParams);
      const response = await apiCall({
        method: "GET",
        url: `/api/v1/whatsapp/campaigns/audience/${qs}`,
        headers: { "Content-Type": "application/json" },
      });
      const data =
        (response as any)?.data?.audience ??
        (response as any)?.data ??
        response;
      return data as CampaignAudiencePreview;
    } catch (error: unknown) {
      return rejectWithValue({ message: getMessage(error) });
    }
  }
);

export const getCampaignWalletEstimate = createAsyncThunk(
  "whatsapp/getWalletEstimate",
  async (
    params: {
      template_id: string;
      album_id?: number | string;
      album_ids?: Array<number | string> | string;
    },
    { rejectWithValue }
  ) => {
    try {
      const queryParams: Record<string, string | number> = {
        template_id: params.template_id,
      };
      if (params.album_id) queryParams.album_id = params.album_id;
      if (params.album_ids) {
        queryParams.album_ids = Array.isArray(params.album_ids)
          ? params.album_ids.join(",")
          : params.album_ids;
      }
      const qs = buildQueryString(queryParams);
      const response = await apiCall({
        method: "GET",
        url: `/api/v1/whatsapp/campaigns/wallet-estimate/${qs}`,
        headers: { "Content-Type": "application/json" },
      });
      const data = (response as { data?: WalletEstimateResponse })?.data ?? response;
      return data as WalletEstimateResponse;
    } catch (error: unknown) {
      return rejectWithValue({ message: getMessage(error) });
    }
  }
);

export const getWhatsAppTemplates = createAsyncThunk(
  "whatsapp/getTemplates",
  async (statusFilter: string | undefined, { rejectWithValue }) => {
    try {
      const qs = statusFilter && statusFilter !== "all" ? `?status=${encodeURIComponent(statusFilter)}` : "";
      const response = await apiCall({
        method: "GET",
        url: `/api/v1/whatsapp/templates/${qs}`,
        headers: { "Content-Type": "application/json" },
      });
      const resData = (response as any)?.data;
      let list: WhatsAppTemplate[] = [];
      if (Array.isArray(resData?.templates)) {
        list = resData.templates;
      } else if (Array.isArray(resData)) {
        list = resData;
      } else if (Array.isArray((response as any)?.results)) {
        list = (response as any).results;
      } else if (Array.isArray((response as any)?.templates)) {
        list = (response as any).templates;
      } else if (Array.isArray(response)) {
        list = response as WhatsAppTemplate[];
      }
      return list;
    } catch (error: unknown) {
      return rejectWithValue({ message: getMessage(error) });
    }
  }
);

export const getWhatsAppTemplateDetail = createAsyncThunk(
  "whatsapp/getTemplateDetail",
  async (templateId: string, { rejectWithValue }) => {
    try {
      const response = await apiCall({
        method: "GET",
        url: `/api/v1/whatsapp/templates/${templateId}/`,
        headers: { "Content-Type": "application/json" },
      });
      const data =
        (response as any)?.data?.template ??
        (response as any)?.data ??
        response;
      return data as WhatsAppTemplate;
    } catch (error: unknown) {
      return rejectWithValue({ message: getMessage(error) });
    }
  }
);

export const createWhatsAppTemplate = createAsyncThunk(
  "whatsapp/createTemplate",
  async (payload: CreateWhatsAppTemplatePayload, { rejectWithValue }) => {
    try {
      const response = await apiCall({
        method: "POST",
        url: "/api/v1/whatsapp/templates/",
        headers: { "Content-Type": "application/json" },
        data: {
          template_name: payload.template_name || payload.name,
          category: payload.category,
          language: payload.language || "en",
          message_purpose: payload.message_purpose,
          body: payload.body,
          sample_values: payload.sample_values || [],
          footer: payload.footer || undefined,
          buttons: payload.buttons && payload.buttons.length > 0 ? payload.buttons : undefined,
        },
      });
      const data =
        (response as any)?.data?.template ??
        (response as any)?.data ??
        response;
      return data as WhatsAppTemplate;
    } catch (error: unknown) {
      return rejectWithValue({ message: getMessage(error) });
    }
  }
);

export const syncWhatsAppTemplate = createAsyncThunk(
  "whatsapp/syncTemplate",
  async (templateId: string, { rejectWithValue }) => {
    try {
      const response = await apiCall({
        method: "POST",
        url: `/api/v1/whatsapp/templates/${templateId}/sync/`,
        headers: { "Content-Type": "application/json" },
      });
      const data =
        (response as any)?.data?.template ??
        (response as any)?.data ??
        response;
      return data as WhatsAppTemplate;
    } catch (error: unknown) {
      return rejectWithValue({ message: getMessage(error) });
    }
  }
);

export const deleteWhatsAppTemplate = createAsyncThunk(
  "whatsapp/deleteTemplate",
  async (payload: { templateId: string; local_only?: boolean }, { rejectWithValue }) => {
    try {
      await apiCall({
        method: "DELETE",
        url: `/api/v1/whatsapp/templates/${payload.templateId}/`,
        headers: { "Content-Type": "application/json" },
        data: payload.local_only ? { local_only: true } : {},
      });
      return payload.templateId;
    } catch (error: unknown) {
      return rejectWithValue({ message: getMessage(error) });
    }
  }
);

export const createRechargeOrder = createAsyncThunk(
  "whatsapp/createRechargeOrder",
  async (payload: CreateRechargeOrderPayload, { rejectWithValue }) => {
    try {
      const response = await apiCall({
        method: "POST",
        url: "/api/v1/wallet/recharge/create-order/",
        headers: { "Content-Type": "application/json" },
        data: payload,
      });
      return response as CreateRechargeOrderResponse;
    } catch (error: unknown) {
      return rejectWithValue({ message: getMessage(error) });
    }
  }
);

export const verifyRecharge = createAsyncThunk(
  "whatsapp/verifyRecharge",
  async (payload: VerifyRechargePayload, { rejectWithValue }) => {
    try {
      const response = await apiCall({
        method: "POST",
        url: "/api/v1/wallet/recharge/verify/",
        headers: { "Content-Type": "application/json" },
        data: payload,
      });
      return response as VerifyRechargeResponse;
    } catch (error: unknown) {
      return rejectWithValue({ message: getMessage(error) });
    }
  }
);

export const cancelRechargeOrder = createAsyncThunk(
  "whatsapp/cancelRechargeOrder",
  async (payload: CancelRechargePayload, { rejectWithValue }) => {
    try {
      const response = await apiCall({
        method: "POST",
        url: "/api/v1/wallet/recharge/cancel/",
        headers: { "Content-Type": "application/json" },
        data: payload,
      });
      return response as CancelRechargeResponse;
    } catch (error: unknown) {
      return rejectWithValue({ message: getMessage(error) });
    }
  }
);

export const getRechargeOrders = createAsyncThunk(
  "whatsapp/getRechargeOrders",
  async (payload: RechargeOrdersListPayload | undefined, { rejectWithValue }) => {
    try {
      const params = {
        page: payload?.page ?? 1,
        page_size: payload?.page_size ?? 20,
        status: payload?.status,
      };
      const qs = buildQueryString(params);
      const response = await apiCall({
        method: "GET",
        url: `/api/v1/wallet/recharge/orders/${qs}`,
        headers: { "Content-Type": "application/json" },
      });
      return response as RechargeOrdersListResponse;
    } catch (error: unknown) {
      return rejectWithValue({ message: getMessage(error) });
    }
  }
);

export const getWhatsAppOnboardingStatusAPI = async () => {
  try {
    const response = await apiCall({
      method: "GET",
      url: "/api/v1/whatsapp/onboarding/",
    });
    return response;
  } catch (error: any) {
    throw error?.response?.data || error;
  }
};

export const submitWhatsAppOnboardingAPI = async (data: {
  use_existing_studio_number: boolean;
  mobile_number?: string;
}) => {
  try {
    const response = await apiCall({
      method: "POST",
      url: "/api/v1/whatsapp/onboarding/",
      data,
    });
    return response;
  } catch (error: any) {
    throw error?.response?.data || error;
  }
};

export const getWhatsAppAccountsAPI = async () => {
  try {
    const response = await apiCall({
      method: "GET",
      url: "/api/v1/whatsapp/accounts/",
    });
    return response;
  } catch (error: any) {
    throw error?.response?.data || error;
  }
};
